import {
  Container,
  FullScreenModal,
  FullScreenModalContainer,
  FullScreenModalHeader,
  FullScreenModalHeading,
  FullScreenModalProps,
} from "@eworkit/iris";
import { FormikHelpers } from "formik";
import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMedia } from "react-use";

import { applyApiErrors } from "@/components/Formik";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { postAdminInvitation } from "@/data/admin-invitations";
import { postConsultantInvitation } from "@/data/consultant-invitations";
import { ConsultantProfile } from "@/data/consultant-profiles/types/ConsultantProfile";
import { postManagerInvitation } from "@/data/manager-invitations";
import { TUserRole } from "@/data/session";
import { Flex, Label } from "@/eworkitComponents";
import { useCompanyContext } from "@/hooks/User";
import { useToasts } from "@/views/components/Toast/useToasts";
import { InvitationRolePickerBox } from "@/views/Invitations/InvitationRolePickerBox";
import { router } from "@/views/router";

import { IInvitationFormValues, InvitationFormik } from "./InvitationForm";

interface IAddNewProfileDialog extends FullScreenModalProps {
  onSuccess?: (profile: ConsultantProfile) => void;
}

export const AddNewProfileDialog: FC<IAddNewProfileDialog> = ({ onSuccess, ...modalProps }) => {
  const Company = useCompanyContext();
  const history = useHistory();
  const { addToast } = useToasts();
  const { t } = useTranslation(["session", "common"]);

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const User = useContext(UserContext);
  const isAdmin = User.hasCompanyAdminPrivileges();

  const [selectedRole, setSelectedRole] = useState<TUserRole | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      setSelectedRole("CONSULTANT");
    }
  }, [isAdmin]);

  const postInvitation = useCallback(
    async (values: IInvitationFormValues) => {
      if (selectedRole === "CONSULTANT") {
        const consultantInvitation = await postConsultantInvitation({ companyId: Company.id }, values);
        return consultantInvitation.consultantProfile;
      } else if (selectedRole === "MANAGER") {
        const managerInvitation = await postManagerInvitation({ companyId: Company.id }, values);
        return managerInvitation.consultantProfile;
      } else if (selectedRole === "ADMIN") {
        const adminInvitation = await postAdminInvitation({ companyId: Company.id }, values);
        return adminInvitation.consultantProfile;
      }

      return;
    },
    [Company.id, selectedRole],
  );

  const closeModal = useCallback(() => {
    if (isAdmin) {
      setSelectedRole(null);
    }
    modalProps.dialogState.hide();
  }, [isAdmin, modalProps.dialogState]);

  const handleSubmit = useCallback(
    async (values: IInvitationFormValues, actions: FormikHelpers<IInvitationFormValues>) => {
      try {
        if (!selectedRole) {
          addToast({
            type: "danger",
            text: t("CONSULTANT_PROFILES.INVITATION.MESSAGES.PERMISSION_LEVEL_NOT_SELECTED"),
          });
          return;
        }

        const profile = await postInvitation(values);

        addToast({
          type: "success",
          text: t("CONSULTANT_PROFILES.INVITATION.MESSAGES.PROFILE_CREATED"),
        });

        closeModal();

        if (onSuccess) {
          onSuccess(profile!);
        } else {
          history.push(
            router.get("consultant-profiles.detail", {
              consultantProfileId: profile!.id,
            }).path,
          );
        }
      } catch (error) {
        addToast({
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
        });
        applyApiErrors(actions, error.payload);
      }
    },
    [selectedRole, postInvitation, addToast, t, closeModal, onSuccess, history],
  );

  return (
    <FullScreenModal dialogState={modalProps.dialogState}>
      <FullScreenModalContainer>
        <FullScreenModalHeader onCloseClick={closeModal} closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}>
          <FullScreenModalHeading>{t(`CONSULTANT_PROFILES.INVITATION.FORM.HEADER`)}</FullScreenModalHeading>
        </FullScreenModalHeader>
        <Container variant={"narrow"}>
          <Flex direction="column" margin={["xl", false, false, false]}>
            <Label required>
              {isAdmin
                ? t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.SELECT_PERMISSION_LEVEL")
                : t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.PERMISSION_LEVEL")}
            </Label>
            <Flex direction={isSmallScreen ? "column" : "row"} innerMargin="md">
              <InvitationRolePickerBox
                header={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MEMBER")}
                description={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MEMBER_DESCRIPTION")}
                onClick={() => setSelectedRole("CONSULTANT")}
                icon="user"
                selected={selectedRole === "CONSULTANT"}
              />
              {isAdmin && (
                <>
                  <InvitationRolePickerBox
                    header={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MANAGER")}
                    description={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MANAGER_DESCRIPTION")}
                    onClick={() => setSelectedRole("MANAGER")}
                    icon="user_settings"
                    selected={selectedRole === "MANAGER"}
                  />
                  <InvitationRolePickerBox
                    header={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.ADMIN")}
                    description={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.ADMIN_DESCRIPTION")}
                    onClick={() => setSelectedRole("ADMIN")}
                    icon="user_hierarchy"
                    selected={selectedRole === "ADMIN"}
                  />
                </>
              )}
            </Flex>

            <Flex margin={["xl", false]}>
              <InvitationFormik onSubmit={handleSubmit} onCancel={closeModal} selectedRole={selectedRole} />
            </Flex>
          </Flex>
        </Container>
      </FullScreenModalContainer>
    </FullScreenModal>
  );
};

export default AddNewProfileDialog;
